import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Pencil, Trash, Plus, X, Crown, ShieldCheck, Key } from "@phosphor-icons/react";
import { toast } from "sonner";

const ALL_PERMS = ["users", "products", "services", "portfolio", "blog", "vlogs", "inbox"];
const PERM_LABELS = {
  users:     "Manage users",
  products:  "Manage products",
  services:  "Manage services",
  portfolio: "Manage portfolio",
  blog:      "Manage blog",
  vlogs:     "Manage vlogs",
  inbox:     "Access inbox",
};

const empty = {
  username: "", email: "", password: "", name: "",
  role: "moderator", permissions: [],
};

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");

  const load = () => api.get("/admin/users").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const togglePerm = (perm) => {
    setEditing((cur) => {
      const next = new Set(cur.permissions || []);
      next.has(perm) ? next.delete(perm) : next.add(perm);
      return { ...cur, permissions: [...next] };
    });
  };

  const save = async (e) => {
    e.preventDefault();
    setError("");
    try {
      if (editing.id) {
        const payload = {
          name: editing.name,
          role: editing.role,
          permissions: editing.permissions,
        };
        if (editing.password) payload.password = editing.password;
        await api.put(`/admin/users/${editing.id}`, payload);
      } else {
        await api.post("/admin/users", editing);
      }
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e2) {
      const d = e2.response?.data?.detail;
      setError(typeof d === "string" ? d : "Save failed");
    }
  };

  const del = async (u) => {
    if (u.is_root) { toast.error("Cannot delete the root admin"); return; }
    if (u.id === me?.id) { toast.error("You can't delete yourself"); return; }
    if (!window.confirm(`Delete user "${u.username}"?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Deleted"); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div data-testid="admin-users">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="display-font text-3xl font-black uppercase">Team & access</h1>
          <p className="text-gray-400 text-sm mt-2">Create admins or moderators and choose what they can manage.</p>
        </div>
        <button onClick={() => { setError(""); setEditing({ ...empty }); }} className="btn-primary" data-testid="new-user-btn">
          <Plus size={18} weight="bold" /> New user
        </button>
      </div>

      <div className="grid gap-3">
        {items.map((u) => (
          <div key={u.id} className="card-dark p-5 flex items-center gap-4" data-testid={`user-row-${u.username}`}>
            <div className="w-10 h-10 flex items-center justify-center border border-[#2a2a35] shrink-0">
              {u.role === "admin" ? <Crown size={20} weight="fill" className="text-[#ccff00]" /> : <ShieldCheck size={20} weight="duotone" className="text-[#00f0ff]" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <div className="display-font font-bold">{u.name || u.username}</div>
                {u.is_root && <span className="mono-font text-[10px] uppercase tracking-widest bg-[#ccff00] text-black px-2 py-0.5">ROOT</span>}
              </div>
              <div className="mono-font text-xs text-gray-500 mt-1 truncate">@{u.username} · {u.email} · {u.role}</div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {(u.permissions && u.permissions.length > 0 ? u.permissions : (u.role === "admin" ? ["all"] : ["—"])).map((p) => (
                  <span key={p} className={`mono-font text-[10px] uppercase tracking-widest px-2 py-1 ${p === "all" ? "bg-[#ccff00] text-black" : "border border-[#2a2a35] text-gray-300"}`}>{p}</span>
                ))}
              </div>
            </div>
            <button
              onClick={() => { setError(""); setEditing({ ...u, password: "" }); }}
              className="p-2 hover:text-[#ccff00]" title="Edit"
              data-testid={`edit-user-${u.username}`}
            ><Pencil size={18} /></button>
            <button
              onClick={() => del(u)}
              disabled={u.is_root || u.id === me?.id}
              className="p-2 hover:text-[#ff003c] disabled:opacity-30 disabled:cursor-not-allowed"
              title="Delete"
              data-testid={`del-user-${u.username}`}
            ><Trash size={18} /></button>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="user-modal">
          <form onSubmit={save} className="bg-[#0a0a0c] border border-[#2a2a35] max-w-2xl w-full max-h-[90vh] overflow-auto p-6">
            <div className="flex justify-between items-center mb-5">
              <h2 className="display-font text-2xl font-bold">{editing.id ? "Edit user" : "Invite new user"}</h2>
              <button type="button" onClick={() => setEditing(null)}><X size={20} /></button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input
                required placeholder="Username" className="input-dark"
                value={editing.username}
                onChange={(e) => setEditing({ ...editing, username: e.target.value })}
                disabled={!!editing.id}
                data-testid="user-username"
              />
              <input
                required type="email" placeholder="Email" className="input-dark"
                value={editing.email}
                onChange={(e) => setEditing({ ...editing, email: e.target.value })}
                disabled={!!editing.id}
                data-testid="user-email"
              />
              <input
                placeholder="Display name" className="input-dark"
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
              />
              <select
                className="input-dark" value={editing.role}
                onChange={(e) => setEditing({ ...editing, role: e.target.value })}
                disabled={editing.is_root}
                data-testid="user-role"
              >
                <option value="moderator">Moderator</option>
                <option value="admin">Admin</option>
              </select>
              <div className="sm:col-span-2 flex items-center gap-2">
                <Key size={18} className="text-gray-500" />
                <input
                  type="password"
                  placeholder={editing.id ? "New password (leave blank to keep current)" : "Password"}
                  className="input-dark flex-1"
                  value={editing.password}
                  onChange={(e) => setEditing({ ...editing, password: e.target.value })}
                  required={!editing.id}
                  minLength={6}
                  data-testid="user-password"
                />
              </div>
            </div>

            <div className="mt-6">
              <div className="label-tag mb-3">Permissions</div>
              <p className="text-xs text-gray-500 mb-3">
                Admins have full access by default. Moderators only get what you tick here.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {ALL_PERMS.map((p) => {
                  const checked = (editing.permissions || []).includes(p);
                  return (
                    <label
                      key={p}
                      className={`flex items-center gap-2 px-3 py-2 border cursor-pointer ${checked ? "bg-[#ccff00]/10 border-[#ccff00] text-[#ccff00]" : "border-[#2a2a35] text-gray-300"}`}
                      data-testid={`perm-${p}`}
                    >
                      <input
                        type="checkbox" checked={checked}
                        onChange={() => togglePerm(p)}
                        className="accent-[#ccff00]"
                      />
                      <span className="mono-font text-xs uppercase tracking-widest">{PERM_LABELS[p]}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            {error && <div className="mono-font text-xs text-[#ff003c] mt-4">{error}</div>}
            <div className="mt-6 flex gap-3 justify-end">
              <button type="button" className="btn-outline" onClick={() => setEditing(null)}>Cancel</button>
              <button type="submit" className="btn-primary" data-testid="save-user">{editing.id ? "Save" : "Create user"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
